import React, { useState, useEffect, useRef } from 'react';
import { Plus, Pencil, Trash2, Lock, Check, X, GripVertical, Image as ImageIcon } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import Modal from '../components/Modal';
import { Field, Input, FormActions } from '../components/FormField';

const BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';
const empty = { id: '', label: '', order: '' };

export default function AdminCategories() {
  const { can, isMasterAdmin } = useAuth();
  const canCreate = isMasterAdmin || can('products', 'create');
  const canEdit   = isMasterAdmin || can('products', 'edit');
  const canDelete = isMasterAdmin || can('products', 'delete');

  const [categories, setCategories] = useState([]);
  const [modal, setModal]     = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm]       = useState(empty);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [errors, setErrors]   = useState({});
  const [loading, setLoading] = useState(false);
  const fileRef = useRef(null);

  const load = () =>
    api.get('/categories/all').then(r => setCategories(r.data.categories || [])).catch(() => {});

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(empty);
    setImageFile(null);
    setImagePreview('');
    setErrors({});
    setModal(true);
  };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ id: c.id, label: c.label, order: String(c.order ?? '') });
    setImageFile(null);
    setImagePreview(c.image || '');
    setErrors({});
    setModal(true);
  };

  const validate = () => {
    const e = {};
    if (!form.label.trim()) e.label = 'Category name is required';
    if (!form.id.trim()) e.id = 'Category ID / slug is required';
    if (form.order !== '' && isNaN(Number(form.order))) e.order = 'Order must be a number';
    return e;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('label', form.label.trim());
      fd.append('id', form.id.trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''));
      if (form.order !== '') fd.append('order', Number(form.order));
      if (imageFile) fd.append('image', imageFile);

      if (editing) {
        const res = await api.put(`/categories/by-id/${editing._id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        if (res.data.pending) {
          toast.info('Category update submitted for Master Admin approval');
        } else {
          toast.success('Category updated');
        }
      } else {
        const res = await api.post('/categories', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        if (res.data.pending) {
          toast.info('Category submitted for Master Admin approval — it will appear after approval');
        } else {
          toast.success('Category created');
        }
      }
      setModal(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error saving category');
    } finally { setLoading(false); }
  };

  const handleToggle = async (cat) => {
    try {
      await api.put(`/categories/${cat.id}`, { isActive: !cat.isActive });
      toast.success(cat.isActive ? 'Category disabled' : 'Category enabled');
      load();
    } catch { toast.error('Error updating category'); }
  };

  const handleDelete = async (cat) => {
    if (!window.confirm(`Delete category "${cat.label}"? Products in this category will NOT be deleted but may lose their category filter.`)) return;
    try {
      await api.delete(`/categories/${cat.id}`);
      toast.success('Category deleted');
      load();
    } catch { toast.error('Error deleting category'); }
  };

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Product Categories</h1>
          <p className="text-sm text-gray-400 mt-0.5">{categories.length} categories — used as filters on the Products page</p>
        </div>
        <button onClick={openCreate} disabled={!canCreate}
          title={!canCreate ? 'No permission' : ''}
          className="flex items-center gap-2 px-4 py-2 text-white text-sm font-semibold rounded-xl shadow-sm transition-all hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ background: canCreate ? 'linear-gradient(135deg, #B8A830, #d4af37)' : '#9CA3AF' }}>
          {canCreate ? <Plus size={14} /> : <Lock size={14} />} Add Category
        </button>
      </div>

      {/* Info banner */}
      <div className="bg-blue-50 border border-blue-100 rounded-xl px-5 py-3 text-xs text-blue-700">
        Categories added here automatically appear as filter buttons on the Products page and in the Add Product form.
        Disabling a category hides it from filters without deleting it.
      </div>

      {/* Category list */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {categories.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-300 gap-3">
            <GripVertical size={32} />
            <p className="text-sm">No categories yet</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider w-16">Image</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Name</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">ID / Slug</th>
                <th className="text-center px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Order</th>
                <th className="text-center px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {categories.map(cat => (
                <tr key={cat.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-5 py-3.5">
                    {cat.image ? (
                      <img
                        src={cat.image.startsWith('http') ? cat.image : `${BASE}${cat.image}`}
                        alt={cat.label}
                        className="w-10 h-10 object-cover rounded-lg border border-gray-200"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                        <ImageIcon size={14} className="text-gray-300" />
                      </div>
                    )}
                  </td>
                  <td className="px-5 py-3.5 font-medium text-gray-800">{cat.label}</td>
                  <td className="px-5 py-3.5 font-mono text-xs text-gray-500 bg-gray-50/50">{cat.id}</td>
                  <td className="px-5 py-3.5 text-center text-gray-500">{cat.order}</td>
                  <td className="px-5 py-3.5 text-center">
                    <button onClick={() => canEdit && handleToggle(cat)} disabled={!canEdit}
                      title={canEdit ? (cat.isActive ? 'Click to disable' : 'Click to enable') : 'No permission'}
                      className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full border transition-colors disabled:cursor-not-allowed
                        ${cat.isActive ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100' : 'bg-gray-100 text-gray-500 border-gray-200 hover:bg-gray-200'}`}>
                      {cat.isActive ? <><Check size={10} /> Active</> : <><X size={10} /> Disabled</>}
                    </button>
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center justify-end gap-2">
                      {canEdit && (
                        <button onClick={() => openEdit(cat)}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-yellow-600 hover:bg-yellow-50 transition-colors">
                          <Pencil size={14} />
                        </button>
                      )}
                      {canDelete && (
                        <button onClick={() => handleDelete(cat)}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors">
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal */}
      {modal && (
        <Modal title={editing ? 'Edit Category' : 'Add New Category'} onClose={() => setModal(false)}>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-xs text-blue-700">
              Categories are used as filter options on the Products page and in the Add Product form.
            </div>

            <Field label="Category Name" required hint='e.g. "Sample Books"' error={errors.label}>
              <Input value={form.label} onChange={e => setForm({ ...form, label: e.target.value })}
                placeholder="e.g. Sample Books" error={errors.label} />
            </Field>

            <Field label="ID / Slug" required
              hint={editing ? 'Changing the slug will update filters. Existing products keep the old slug.' : 'Unique identifier, e.g. "sample-books". Auto-lowercased.'}
              error={errors.id}>
              <Input value={form.id} onChange={e => setForm({ ...form, id: e.target.value })}
                placeholder="e.g. sample-books" error={errors.id} />
            </Field>

            <Field label="Display Order" hint="Lower numbers appear first" error={errors.order}>
              <Input type="number" min="0" value={form.order}
                onChange={e => setForm({ ...form, order: e.target.value })}
                placeholder="0" error={errors.order} />
            </Field>

            {/* Category Image */}
            <Field label="Category Cover Image" hint="Shown on the home page category cards. JPG/PNG recommended.">
              <div className="space-y-2">
                {imagePreview && (
                  <div className="relative inline-block">
                    <img
                      src={imagePreview.startsWith('http') || imagePreview.startsWith('blob') ? imagePreview : `${BASE}${imagePreview}`}
                      alt="Preview"
                      className="w-full h-36 object-cover rounded-lg border border-gray-200"
                    />
                    <button
                      type="button"
                      onClick={() => { setImageFile(null); setImagePreview(''); if (fileRef.current) fileRef.current.value = ''; }}
                      className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center">
                      <X size={10} />
                    </button>
                  </div>
                )}
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  onChange={e => {
                    const f = e.target.files?.[0];
                    if (f) { setImageFile(f); setImagePreview(URL.createObjectURL(f)); }
                  }}
                  className="text-sm text-gray-600 w-full"
                />
              </div>
            </Field>

            <FormActions onCancel={() => setModal(false)} loading={loading}
              submitLabel={editing ? 'Update Category' : 'Add Category'} />
          </form>
        </Modal>
      )}
    </div>
  );
}
